/**
 * ZAGX v2 — Client-Side Price API Proxy
 *
 * Replaces direct TD/CG/BIST API calls with a single /api/prices call.
 * Falls back to direct client-side fetching when API is unavailable.
 *
 * Usage: import { fetchPricesFromAPI } from './price-api.js'
 * The result has same shape as what refreshPrices() expects.
 */

import { fetchWithRetry, breakers } from './resilience.js';

const API_BASE = '/api'; // Netlify functions or Express

/**
 * Check if server API is available (delegated to circuit breaker)
 * The breaker.api manages cooldown/retry internally — no more manual _apiAvailable tracking.
 */
async function checkApiAvailable() {
  if (!breakers.api.canRequest()) return false;

  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(`${API_BASE}/health`, { signal: ctrl.signal });
    clearTimeout(timer);
    const d = await r.json();
    if (d.status === 'ok') {
      breakers.api.recordSuccess();
      console.log('[ZAGX] API: available');
      return true;
    }
    breakers.api.recordFailure();
    console.log('[ZAGX] API: not available (bad status)');
    return false;
  } catch {
    breakers.api.recordFailure();
    console.log('[ZAGX] API not available — using client-side fallback');
    return false;
  }
}

/**
 * Fetch prices via server API
 * @param {string[]} tickers - Array of ticker symbols
 * @returns {Object|null} { prices, fx, stats } or null if API unavailable
 */
export async function fetchPricesFromAPI(tickers) {
  const available = await checkApiAvailable();
  if (!available) return null;

  try {
    const url = `${API_BASE}/prices?tickers=${tickers.join(',')}`;
    const r = await fetchWithRetry(url, {
      timeout: 60000,
      retries: 1,
      backoffBase: 2000,
      breaker: breakers.api,
      onRetry: (attempt, err) => console.log('[API] Price retry', attempt, ':', err.message)
    });
    const data = await r.json();
    return data;
  } catch (e) {
    console.warn('[ZAGX] API price fetch failed, falling back to client:', e.message);
    return null;
  }
}

/**
 * Fetch FX rates via server API
 * @returns {Object|null} { usdtry, eurtry, eurusd, ts } or null
 */
export async function fetchFxFromAPI() {
  const available = await checkApiAvailable();
  if (!available) return null;

  try {
    const r = await fetchWithRetry(`${API_BASE}/fx`, {
      timeout: 8000,
      retries: 1,
      backoffBase: 1000,
      breaker: breakers.api,
      onRetry: (attempt, err) => console.log('[API] FX retry', attempt, ':', err.message)
    });
    return await r.json();
  } catch (e) {
    console.warn('[ZAGX] API FX fetch failed:', e.message);
    return null;
  }
}

/**
 * Reset API availability check (e.g., after settings change)
 */
export function resetApiCheck() {
  breakers.api.reset();
}
